import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { LazyScrollableDataSource } from '@rappi/common';

import { SelectOption } from '../../../purchase-order-detail/sections/section-base.types';
import { FhOptionResolver } from '../../../shared/fh-select/fh-select.component';
import {
  FH_ERROR_MESSAGE,
  FH_FIELD,
  FH_FIELD_ERROR,
  FH_FIELD_TYPE,
  FH_FIELD_VALIDATION,
  FH_SERVICE_TYPE,
  FH_WARN,
  fhTaxIdFormatMessage,
} from '../../company-form.constants';
import { CompanyFieldDescriptor, FhFieldState } from '../../company-form.types';

// Renders one merged field descriptor as the right @rappi/ui control.
//
// This is the classifier's view half: an ngSwitch over `field.type`. Dynamic components were
// considered and rejected — `formControlName` needs the template's ControlContainer ancestor, and
// Angular 10 would also require entryComponents registration per type. The type set is closed
// (six types, driven by a backend enum), so a switch keeps templates statically analysable and
// the bindings free.
//
// Unlike the reference implementation this switch has no path that yields `undefined`: an
// unrecognised type falls through to a plain text input and warns once.
@Component({
  selector: 'app-company-field',
  templateUrl: './company-field.component.html',
  styleUrls: ['./company-field.component.scss'],
})
export class CompanyFieldComponent implements OnInit {
  /* istanbul ignore next -- decorator metadata */
  @Input() field: CompanyFieldDescriptor;
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() state: FhFieldState | null = null;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;
  /** Remote option source; null for static selects, which use `field.options`. */
  /* istanbul ignore next -- decorator metadata */
  @Input() dataSource: LazyScrollableDataSource<SelectOption> | null = null;
  /** Resolves ids a remote source has not loaded yet (edit-mode prefill). */
  /* istanbul ignore next -- decorator metadata */
  @Input() optionResolver: FhOptionResolver | null = null;
  /** Session country (DEV -> CO), upper-case — only consumed to build the tax_id format message. */
  /* istanbul ignore next -- decorator metadata */
  @Input() country = '';
  /** Current value of `Is International Company?` — the tax_id format message switches to IDE for it. */
  /* istanbul ignore next -- decorator metadata */
  @Input() isInternationalCompany = false;

  /* istanbul ignore next -- decorator metadata */
  @Output() underInputClick = new EventEmitter<CompanyFieldDescriptor>();
  /* istanbul ignore next -- decorator metadata */
  @Output() editOptionClick = new EventEmitter<{ field: CompanyFieldDescriptor; option: SelectOption }>();

  readonly TYPE = FH_FIELD_TYPE;
  readonly SERVICE_TYPE = FH_SERVICE_TYPE;

  ngOnInit(): void {
    const known: string[] = [
      FH_FIELD_TYPE.SELECT, FH_FIELD_TYPE.STRING, FH_FIELD_TYPE.NUMBER,
      FH_FIELD_TYPE.TEXTAREA, FH_FIELD_TYPE.BOOLEAN, FH_FIELD_TYPE.FILE,
    ];
    if (this.field && known.indexOf(this.field.type) === -1) {
      console.warn(FH_WARN.UNKNOWN_FIELD_TYPE, this.field.type);
    }
  }

  // Required fields carry a trailing asterisk, matching the Figma labels ("Commercial name*").
  get label(): string {
    const required = this.state ? this.state.required : this.field.required;
    return required ? `${this.field.label}*` : this.field.label;
  }

  // `one-input` forwards [type] to the native input, so email/url get browser affordances on top
  // of the validators built from the same config.
  get inputType(): string {
    if (this.field.validation === FH_FIELD_VALIDATION.EMAIL) {
      return 'email';
    }
    if (this.field.validation === FH_FIELD_VALIDATION.URL) {
      return 'url';
    }
    return 'text';
  }

  get step(): string | null {
    return this.field.decimals ? String(Math.pow(10, -this.field.decimals)) : null;
  }

  get searchable(): boolean {
    // Remote sources are always searchable; long static lists benefit from it too.
    return !!this.field.service || this.field.options.length > 10;
  }

  get control() {
    return this.form.get(this.field.controlName);
  }

  // Errors only surface after a submit attempt or once the user has touched the control, so a
  // pristine form is not a wall of red.
  get showErrors(): boolean {
    const ctrl = this.control;
    return !!ctrl && !!ctrl.errors && (this.submitted || ctrl.touched);
  }

  get errorMessage(): string | null {
    const ctrl = this.control;
    if (!this.showErrors || !ctrl?.errors) {
      return null;
    }
    const key = Object.keys(ctrl.errors)[0];
    // The Tax ID fails the (permissive) backend regex as FH_FIELD_ERROR.PATTERN like any other
    // field, but "Invalid format." doesn't tell the user the shape to enter. Swap in the
    // country-specific copy (PRD HU4 D.1), falling back to the generic message when the country
    // has no known format.
    if (key === FH_FIELD_ERROR.PATTERN && this.field.fieldId === FH_FIELD.TAX_ID) {
      const taxIdMessage = fhTaxIdFormatMessage(this.country, this.isInternationalCompany);
      if (taxIdMessage) {
        return taxIdMessage;
      }
    }
    return FH_ERROR_MESSAGE[key] ?? FH_ERROR_MESSAGE['required'];
  }

  onUnderInputClick(): void {
    this.underInputClick.emit(this.field);
  }

  onFhEditOption(option: SelectOption): void {
    this.editOptionClick.emit({ field: this.field, option });
  }
}
